import { useMemo } from 'react'

import type { VatDeclarationsReport } from '@features/vatDeclarations/types/vatDeclarationsReport'
import type { VatDeclarationsReportField } from '@features/vatDeclarations/types/vatDeclarationsReportField'

import type { ReportSection } from '../types/reportSection'

export type SplitNameSectionParams = {
  symbol: string | null
  name: string | null | undefined
  fields: VatDeclarationsReportField[]
}

export const splitNameSection = ({ symbol, name, fields }: SplitNameSectionParams): ReportSection[] => {
  if (!name) {
    return [{ fields }]
  }
  const [sectionName, subSectionName] = name.split(' - ')
  const mainGroupName = sectionName || name

  return [
    {
      mainHeader: { symbol, title: mainGroupName },
      subheader: {
        leftText: subSectionName || ' ',
        fieldsForLabels: fields,
      },
      fields,
      reserveSymbolColumn: Boolean(symbol),
    },
  ]
}

export const useFormattedReport = (report: VatDeclarationsReport, countryId?: string) => {
  return useMemo(() => {
    const blocks: ReportSection[] = []

    if (countryId === 'FR') {
      for (const item of report) {
        if ('sections' in item) {
          const { symbol, name, sections } = item
          if (name) {
            blocks.push({
              mainHeader: { symbol, title: name },
              fields: [],
            })
          }

          for (const sec of sections) {
            const { name: secName, fields } = sec
            blocks.push({
              subheader: { leftText: secName ?? ' ', fieldsForLabels: fields },
              fields,
              reserveSymbolColumn: Boolean(symbol),
            })
          }
        } else if ('fields' in item) {
          const { symbol, name, fields } = item
          blocks.push({
            ...(name ? { mainHeader: { symbol, title: name } } : {}),
            subheader: { leftText: ' ', fieldsForLabels: fields },
            fields,
            reserveSymbolColumn: Boolean(name && symbol),
          })
        }
      }
      return blocks
    }

    for (const item of report) {
      if ('sections' in item) {
        if (item.name) {
          blocks.push({
            mainHeader: { symbol: item.symbol, title: item.name },
            fields: [],
          })
        }

        for (const sec of item.sections) {
          const { symbol, name, fields } = sec
          blocks.push(...splitNameSection({ symbol, name, fields }))
        }
      } else if ('fields' in item) {
        const { symbol, name, fields } = item
        blocks.push(...splitNameSection({ symbol, name, fields }))
      }
    }

    return blocks
  }, [report, countryId])
}
